import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { filesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const UploadPage = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFile = async (file) => {
    if (!file) return;
    
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setProgress(0);
    setUploadResult(null);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      const response = await filesApi.upload(file);
      clearInterval(progressInterval);
      setProgress(100);
      setUploadResult({
        success: true,
        ...response.data
      });
      toast.success('File uploaded and processed successfully!');
    } catch (error) {
      clearInterval(progressInterval);
      setProgress(0);
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="upload-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Button>
        <div>
          <h1 className="font-display text-3xl font-light text-slate-900">Upload Data</h1>
          <p className="text-slate-500 mt-1">Import your Excel spreadsheets for analysis</p>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="border border-slate-200 shadow-none rounded-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
            Excel File Upload
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Drop Zone */}
          <div
            className={`
              border-2 border-dashed rounded-sm p-12 text-center transition-colors cursor-pointer
              ${dragActive ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleChange}
              className="hidden"
              data-testid="file-input"
            />
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-slate-100 rounded-sm flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <p className="text-lg font-medium text-slate-900 mb-2">
                Drop your Excel file here
              </p>
              <p className="text-slate-500 text-sm mb-4">
                or click to browse
              </p>
              <p className="text-xs text-slate-400">
                Supported formats: .xlsx, .xls (max 10MB)
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="mt-6" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Processing file...</span>
                <span className="text-sm font-mono text-slate-900">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div 
              className={`mt-6 p-4 rounded-sm border ${
                uploadResult.success 
                  ? 'bg-emerald-50 border-emerald-100' 
                  : 'bg-red-50 border-red-100'
              }`}
              data-testid="upload-result"
            >
              <div className="flex items-start gap-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" strokeWidth={1.5} />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" strokeWidth={1.5} />
                )}
                <div className="flex-1">
                  {uploadResult.success ? (
                    <>
                      <p className="font-medium text-emerald-900">{uploadResult.message}</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        File: {uploadResult.filename}
                      </p>
                      <p className="text-sm text-emerald-700">
                        Sheets parsed: {uploadResult.sheets?.length || 0}
                      </p>
                      {uploadResult.sheets && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {uploadResult.sheets.slice(0, 6).map((sheet, i) => (
                            <span 
                              key={i}
                              className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-sm"
                            >
                              {sheet}
                            </span>
                          ))}
                          {uploadResult.sheets.length > 6 && (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-sm">
                              +{uploadResult.sheets.length - 6} more
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-4 flex gap-3">
                        <Button 
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                          onClick={() => navigate(`/files/${uploadResult.id}`)}
                          data-testid="view-file-btn"
                        >
                          View Data
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="rounded-sm"
                          onClick={() => navigate('/insights')}
                          data-testid="generate-insights-btn"
                        >
                          Generate AI Insights
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-red-900">{uploadResult.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="mt-6 border border-slate-200 shadow-none rounded-sm">
        <CardContent className="p-6">
          <h3 className="font-display text-lg font-medium text-slate-900 mb-4">
            Supported Data Formats
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="font-medium text-slate-900 mb-2">Budget vs Actuals</p>
              <p className="text-slate-600">
                Sheets with columns containing "Budget", "Actual", "Variance" headers
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="font-medium text-slate-900 mb-2">Cashflow Data</p>
              <p className="text-slate-600">
                Monthly forecasted and actual cashflow with cumulative totals
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="font-medium text-slate-900 mb-2">Multi-Sheet Workbooks</p>
              <p className="text-slate-600">
                We'll parse all sheets and identify financial data patterns
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="font-medium text-slate-900 mb-2">Historical Data</p>
              <p className="text-slate-600">
                Upload multiple files to track changes over time
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadPage;
